import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { addConversationTrainingLabel } from "./schema/add-conversation-training-label";
import { completeSelfServeOnboarding } from "./schema/complete-self-serve-onboarding";
import { completeSelfServeOnboardingV2 } from "./schema/complete-self-serve-onboarding-v2";
import { conciergeAnalytics } from "./schema/concierge-analytics";
import { createConciergeKnowledge } from "./schema/create-concierge-knowledge";
import { deleteConciergeKnowledge } from "./schema/delete-concierge-knowledge";
import { generateConciergeBehavior } from "./schema/generate-concierge-behavior";
import { getConciergeDashboardProductTableData } from "./schema/get-concierge-dashboard-product-table-data";
import { getConciergeGeneralSettings } from "./schema/get-concierge-general-settings";
import { getConciergeKnowledge } from "./schema/get-concierge-knowledge";
import { getConciergeResponse } from "./schema/get-concierge-response";
import { getConciergeSkillsSettings } from "./schema/get-concierge-skills-settings";
import { getConciergeContextSettings } from "./schema/get-context-settings";
import { getConversationById } from "./schema/get-conversation-by-id";
import { getConversationTrainingLabelsForTeam } from "./schema/get-conversation-training-labels-for-team";
import { getConversations } from "./schema/get-conversations";
import { getConversationsByIds } from "./schema/get-conversations-by-ids";
import { getDemoConciergeKnowledge } from "./schema/get-demo-concierge-knowledge";
import { getProductSettings } from "./schema/get-product-settings";
import { getSuggestedKnowledge } from "./schema/get-suggested-knowledge";
import { getTeamConciergeKnowledge } from "./schema/get-team-concierge-knowledge";
import { scrapeConciergeKnowledge } from "./schema/scrape-concierge-knowledge";
import { setConciergeContextSettings } from "./schema/set-context-settings";
import { supportAnalytics } from "./schema/support-analytics";
import { updateConciergeGeneralSettings } from "./schema/update-concierge-general-settings";
import { updateConciergeKnowledge } from "./schema/update-concierge-knowledge";
import { updateConciergeSkillsSettings } from "./schema/update-concierge-skills-settings";
import { updateProductSettings } from "./schema/update-product-settings";
import { upsertDemoConciergeKnowledge } from "./schema/upsert-demo-concierge-knowledge";

type RpcDefinitionType = {
  scrapeConciergeKnowledge: typeof scrapeConciergeKnowledge;
  updateConciergeGeneralSettings: typeof updateConciergeGeneralSettings;
  getConciergeGeneralSettings: typeof getConciergeGeneralSettings;
  getConciergeKnowledge: typeof getConciergeKnowledge;
  getConciergeSkillsSettings: typeof getConciergeSkillsSettings;
  updateConciergeSkillsSettings: typeof updateConciergeSkillsSettings;
  getConversations: typeof getConversations;
  getConciergeContextSettings: typeof getConciergeContextSettings;
  setConciergeContextSettings: typeof setConciergeContextSettings;
  getConciergeDashboardProductTableData: typeof getConciergeDashboardProductTableData;
  getProductSettings: typeof getProductSettings;
  updateProductSettings: typeof updateProductSettings;
  getConversationById: typeof getConversationById;
  getConversationsByIds: typeof getConversationsByIds;
  generateConciergeBehavior: typeof generateConciergeBehavior;
  addConversationTrainingLabel: typeof addConversationTrainingLabel;
  getConversationTrainingLabelsForTeam: typeof getConversationTrainingLabelsForTeam;
  createConciergeKnowledge: typeof createConciergeKnowledge;
  updateConciergeKnowledge: typeof updateConciergeKnowledge;
  deleteConciergeKnowledge: typeof deleteConciergeKnowledge;
  getTeamConciergeKnowledge: typeof getTeamConciergeKnowledge;
  getSuggestedKnowledge: typeof getSuggestedKnowledge;
  getConciergeResponse: typeof getConciergeResponse;
  getDemoConciergeKnowledge: typeof getDemoConciergeKnowledge;
  upsertDemoConciergeKnowledge: typeof upsertDemoConciergeKnowledge;
  completeSelfServeOnboarding: typeof completeSelfServeOnboarding;
  completeSelfServeOnboardingV2: typeof completeSelfServeOnboardingV2;
  conciergeAnalytics: typeof conciergeAnalytics;
  supportAnalytics: typeof supportAnalytics;
};

export const rpcDefinition: RpcDefinitionType = {
  scrapeConciergeKnowledge,
  updateConciergeGeneralSettings,
  getConciergeGeneralSettings,
  getConciergeKnowledge,
  getConciergeSkillsSettings,
  updateConciergeSkillsSettings,
  getConversations,
  getConciergeContextSettings,
  setConciergeContextSettings,
  getConciergeDashboardProductTableData,
  getProductSettings,
  updateProductSettings,
  getConversationById,
  getConversationsByIds,
  generateConciergeBehavior,
  addConversationTrainingLabel,
  getConversationTrainingLabelsForTeam,
  createConciergeKnowledge,
  updateConciergeKnowledge,
  deleteConciergeKnowledge,
  getTeamConciergeKnowledge,
  getSuggestedKnowledge,
  getConciergeResponse,
  getDemoConciergeKnowledge,
  upsertDemoConciergeKnowledge,
  completeSelfServeOnboarding,
  completeSelfServeOnboardingV2,
  conciergeAnalytics,
  supportAnalytics,
};

export type RedoConciergeRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
